/**
 * Feature Flag API Service
 *
 * This module provides methods to interact with the feature flag backend endpoints.
 * Used to control AI-rating functionality and other feature flags in the system.
 */

import requester from './requester';

const { request } = requester;

/**
 * Get all feature flags
 * @returns {Promise} Response with list of all feature flags
 */
export function getFeatureFlags() {
  return request({
    url: '/featureFlag/list',
    method: 'GET'
  });
}

/**
 * Get a specific feature flag by key
 * @param {string} featureKey - The feature key (e.g., 'AI_RATING_ENABLED')
 * @returns {Promise} Response with the specific feature flag
 */
export function getFeatureFlag(featureKey) {
  return request({
    url: `/featureFlag/${featureKey}`,
    method: 'GET'
  });
}

/**
 * Check if a feature is enabled
 * @param {string} featureKey - The feature key (e.g., 'AI_RATING_ENABLED')
 * @returns {Promise} Response with boolean indicating if feature is enabled
 */
export function checkFeatureEnabled(featureKey) {
  return request({
    url: `/featureFlag/check/${featureKey}`,
    method: 'GET'
  });
}

/**
 * Enable a feature flag
 * @param {string} featureKey - The feature key to enable
 * @returns {Promise} Response with success status
 */
export function enableFeatureFlag(featureKey) {
  return request({
    url: `/featureFlag/enable/${featureKey}`,
    method: 'POST'
  });
}

/**
 * Disable a feature flag
 * @param {string} featureKey - The feature key to disable
 * @returns {Promise} Response with success status
 */
export function disableFeatureFlag(featureKey) {
  return request({
    url: `/featureFlag/disable/${featureKey}`,
    method: 'POST'
  });
}

/**
 * Update feature flag configuration
 * @param {Object} featureFlag - The feature flag object to update
 * @returns {Promise} Response with success status
 */
export function updateFeatureFlag(featureFlag) {
  return request({
    url: '/featureFlag/update',
    method: 'PUT',
    data: featureFlag
  });
}

/**
 * Feature flag keys constants
 */
export const FEATURE_FLAG_KEYS = {
  AI_RATING_ENABLED: 'AI_RATING_ENABLED',
  AI_RATING_MAX_RETRY: 'AI_RATING_MAX_RETRY',
  AI_RATING_CIRCUIT_BREAKER_THRESHOLD: 'AI_RATING_CIRCUIT_BREAKER_THRESHOLD'
};

export default {
  getFeatureFlags,
  getFeatureFlag,
  checkFeatureEnabled,
  enableFeatureFlag,
  disableFeatureFlag,
  updateFeatureFlag,
  FEATURE_FLAG_KEYS
};